import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, FileUp, Check, ArrowRight, Database, Loader2, CheckCheck, X, Upload, Sparkles } from 'lucide-react';
import { TallyImport } from '@/components/TallyImport';
import { InvoiceUpload } from '@/components/InvoiceUpload';
import { MatchWeightsConfig } from '@/components/MatchWeightsConfig';
import { ExportDialog } from '@/components/ExportDialog';
import { findMatches, type MatchWeights, type Match } from '@/lib/matching';
import { csvUploadSchema } from '@/lib/validation';
import { parseFlexibleDate } from '@/lib/dateUtils';
import { errorLogger } from '@/lib/errorLogger';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { SEO } from '@/components/SEO';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

export default function Sync() {
  const { user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [bankCount, setBankCount] = useState(0);
  const [tallyCount, setTallyCount] = useState(0);
  const [matchWeights, setMatchWeights] = useState<MatchWeights>({
    amount: 0.5,
    date: 0.2,
    narration: 0.3
  });
  const { limits, isWithinLimit: checkPlanLimit } = usePlanLimits();
  const { usage, incrementTransactions } = useUsageTracking();

  useEffect(() => {
    if (!user) return;
    fetchMatches();
  }, [user]);

  const fetchMatches = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [bankData, tallyData] = await Promise.all([
        supabase.from('bank_transactions').select('*').eq('user_id', user.id).eq('status', 'unmatched'),
        supabase.from('tally_entries').select('*').eq('user_id', user.id).eq('status', 'unmatched')
      ]);

      setBankCount(bankData.data?.length || 0);
      setTallyCount(tallyData.data?.length || 0);

      const suggestedMatches = findMatches(
        bankData.data || [],
        tallyData.data || [],
        matchWeights
      );

      setMatches(suggestedMatches);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !user) return;
    
    setImporting(true);
    try {
      const file = e.target.files[0];
      csvUploadSchema.parse({ file });
      const text = await file.text();
      const rows = text.split('\n').map(row => row.split(','));
      const headers = rows[0].map(h => h.toLowerCase().trim());
      
      const transactions = rows.slice(1).map(row => {
        const obj: any = {};
        headers.forEach((h, i) => obj[h] = row[i]?.trim());
        
        const rawAmount = obj.amount || obj.debit || obj.credit || '0';
        const amount = Math.abs(parseFloat(String(rawAmount).replace(/[^0-9.-]/g, '')) || 0);
        
        const rawDate = obj.date || obj.transaction_date || obj.txn_date || '';
        const parsedDate = parseFlexibleDate(rawDate);
        
        const narration = (obj.narration || obj.description || obj.particulars || obj.remarks || 'Imported').substring(0, 500);
        
        if (amount <= 0 || !narration) return null;
        
        return {
          user_id: user.id,
          date: parsedDate,
          amount,
          narration,
          status: 'unmatched' as const,
          bank_name: obj.bank_name || obj.bank || 'Imported CSV'
        };
      }).filter((t): t is NonNullable<typeof t> => t !== null);

      if (transactions.length === 0) {
        toast.error('No valid transactions found in CSV');
        return;
      }

      const newTotal = usage.transactionsImported + transactions.length;
      if (!checkPlanLimit('transactions', usage.transactionsImported) || 
          (limits.transactions !== -1 && newTotal > limits.transactions)) {
        const remaining = limits.transactions === -1 ? 'unlimited' : Math.max(0, limits.transactions - usage.transactionsImported);
        toast.error(`You can only import ${remaining} more transactions this month. Please upgrade.`);
        return;
      }

      const { error } = await supabase.from('bank_transactions').insert(transactions);
      
      if (error) {
        errorLogger.trackImport('csv', false, transactions.length, error.message);
        throw error;
      }
      
      await supabase.from('logs').insert({
        user_id: user.id,
        msg: `Imported ${transactions.length} transactions`,
        level: 'success',
        time: new Date().toLocaleTimeString()
      });

      await incrementTransactions(transactions.length);
      errorLogger.trackImport('csv', true, transactions.length);

      toast.success(`Imported ${transactions.length} transactions`);
      fetchMatches();
    } catch (error: any) {
      errorLogger.error(`CSV import failed: ${error.message}`, 'Sync', error);
      toast.error(error.message);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const confirmMatch = async (match: Match) => {
    if (!user) return;

    await Promise.all([
      supabase.from('bank_transactions').update({ status: 'matched' }).eq('id', match.bankTxn.id),
      supabase.from('tally_entries').update({ status: 'matched' }).eq('id', match.tallyEntry.id),
      supabase.from('match_history').insert({
        user_id: user.id,
        bank_transaction_id: match.bankTxn.id,
        tally_entry_id: match.tallyEntry.id,
        match_score: match.score
      }),
      supabase.from('logs').insert({
        user_id: user.id,
        msg: `Reconciled: ${match.bankTxn.narration}`,
        level: 'success',
        time: new Date().toLocaleTimeString()
      })
    ]);

    toast.success('Match confirmed');
    setSelectedIds(prev => prev.filter(id => id !== match.id));
    fetchMatches();
  };

  const confirmSelectedMatches = async () => {
    if (selectedIds.length === 0) return;
    setConfirmingAll(true);
    
    try {
      const selectedMatches = matches.filter(m => selectedIds.includes(m.id));
      for (const match of selectedMatches) {
        await confirmMatch(match);
      }
      toast.success(`Confirmed ${selectedIds.length} matches`);
      setSelectedIds([]);
    } finally {
      setConfirmingAll(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === matches.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(matches.map(m => m.id));
    }
  };

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const hasNoData = bankCount === 0 && tallyCount === 0;

  return (
    <div className="min-h-full bg-background">
      <SEO title="Sync Engine - Sentinel" description="Bank reconciliation and data import" />
      
      <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-3xl bg-gradient-primary shadow-glow">
              <RefreshCw className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Sync Engine</h1>
              <p className="text-sm text-muted-foreground">Import data and reconcile transactions</p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <MatchWeightsConfig 
              weights={matchWeights} 
              onSave={(newWeights) => {
                setMatchWeights(newWeights);
                fetchMatches();
              }} 
            />
            <ExportDialog />
            <label className="flex-1 sm:flex-none">
              <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} />
              <Button variant="outline" disabled={importing} className="w-full sm:w-auto cursor-pointer rounded-2xl" asChild>
                <span className="flex items-center justify-center gap-2">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span>{importing ? 'Importing...' : 'Import CSV'}</span>
                </span>
              </Button>
            </label>
          </div>
        </motion.div>

        {/* Usage Warning */}
        <UpgradePrompt type="transactions" />

        {/* Stats Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <Card className="p-5 rounded-3xl">
            <div className="text-3xl font-bold text-foreground">{bankCount}</div>
            <div className="text-sm text-muted-foreground mt-1">Bank Transactions</div>
          </Card>
          <Card className="p-5 rounded-3xl">
            <div className="text-3xl font-bold text-foreground">{tallyCount}</div>
            <div className="text-sm text-muted-foreground mt-1">Ledger Entries</div>
          </Card>
          <Card className="p-5 rounded-3xl">
            <div className="text-3xl font-bold text-primary">{matches.length}</div>
            <div className="text-sm text-muted-foreground mt-1">Suggested Matches</div>
          </Card>
          <Card className="p-5 rounded-3xl">
            <div className="text-3xl font-bold text-success">{selectedIds.length}</div>
            <div className="text-sm text-muted-foreground mt-1">Selected</div>
          </Card>
        </motion.div>

        {/* Import Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="rounded-3xl">
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="h-5 w-5" />
                Tally Import
              </CardTitle>
              <CardDescription>Upload Tally XML voucher exports for reconciliation</CardDescription>
            </CardHeader>
            <CardContent>
              <TallyImport />
            </CardContent>
          </Card>
        </motion.div>

        {/* Invoice Upload Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <InvoiceUpload onSuccess={() => {
            toast.success('Invoice added successfully');
          }} />
        </motion.div>

        {/* Empty State */}
        {hasNoData && !loading && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className="p-12 text-center rounded-3xl border-dashed">
              <div className="w-20 h-20 rounded-3xl bg-surface mx-auto mb-6 flex items-center justify-center">
                <RefreshCw className="h-10 w-10 text-muted-foreground/50" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Get Started with Reconciliation</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Import your bank statements using "Import CSV" and upload Tally entries to automatically match and reconcile transactions.
              </p>
              <label>
                <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} />
                <Button className="rounded-2xl" asChild>
                  <span className="cursor-pointer">
                    <Upload className="h-4 w-4 mr-2" />
                    Import Bank Statement
                  </span>
                </Button>
              </label>
            </Card>
          </motion.div>
        )}

        {/* Bulk Actions */}
        <AnimatePresence>
          {matches.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-surface rounded-3xl border border-border/50"
            >
              <div className="flex items-center gap-3">
                <Checkbox 
                  checked={selectedIds.length === matches.length && matches.length > 0}
                  onCheckedChange={selectAll}
                />
                <span className="text-sm text-muted-foreground">
                  {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
                </span>
              </div>
              {selectedIds.length > 0 && (
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedIds([])}
                    className="flex-1 sm:flex-none rounded-2xl"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={confirmSelectedMatches}
                    disabled={confirmingAll}
                    className="flex-1 sm:flex-none rounded-2xl"
                  >
                    {confirmingAll ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCheck className="h-4 w-4 mr-1" />
                    )}
                    Confirm All
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Matches List */}
        {!loading && matches.length > 0 && (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {matches.map((match, idx) => (
                <motion.div
                  key={match.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: idx * 0.03 }}
                >
                  <Card 
                    className={cn(
                      "p-5 rounded-3xl transition-all hover:shadow-card-hover",
                      selectedIds.includes(match.id) && "ring-2 ring-primary bg-primary/5"
                    )}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox 
                        checked={selectedIds.includes(match.id)}
                        onCheckedChange={() => toggleSelect(match.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        {/* Bank Transaction */}
                        <div className="mb-4">
                          <div className="text-xs uppercase text-muted-foreground font-medium mb-1">Bank</div>
                          <div className="text-sm text-foreground font-medium line-clamp-1">{match.bankTxn.narration}</div>
                          <div className="text-sm text-muted-foreground">{formatCurrency(match.bankTxn.amount)}</div>
                        </div>
                        
                        {/* Arrow and Score */}
                        <div className="flex items-center gap-3 mb-4">
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          <Badge variant="secondary" className="bg-success/10 text-success border-success/20 rounded-xl">
                            {Math.round(match.score * 100)}% Match
                          </Badge>
                          <span className="text-xs text-muted-foreground line-clamp-1 hidden sm:inline">
                            {match.reasons.slice(0, 2).join(' • ')}
                          </span>
                        </div>
                        
                        {/* Tally Entry */}
                        <div>
                          <div className="text-xs uppercase text-muted-foreground font-medium mb-1">Ledger</div>
                          <div className="text-sm text-foreground font-medium line-clamp-1">{match.tallyEntry.narration}</div>
                          <div className="text-sm text-muted-foreground">{formatCurrency(match.tallyEntry.amount)}</div>
                        </div>
                      </div>
                      
                      <Button 
                        onClick={() => confirmMatch(match)} 
                        size="icon" 
                        variant="outline" 
                        className="shrink-0 border-success/50 hover:bg-success/10 rounded-2xl w-12 h-12"
                      >
                        <Check className="h-5 w-5 text-success" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* No Matches */}
        {!loading && !hasNoData && matches.length === 0 && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className="p-12 text-center rounded-3xl border-dashed">
              <Sparkles className="h-16 w-16 mx-auto mb-6 text-success" />
              <h3 className="text-xl font-semibold text-foreground mb-3">All Caught Up!</h3>
              <p className="text-muted-foreground">
                No pending matches found. Import more data to continue reconciliation.
              </p>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
